import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CAMPOS_DA_IMPORTACAO,
  CAMPOS_OBRIGATORIOS_DA_IMPORTACAO,
  DIAGNOSTICOS,
  ROTULO_CAMPO_DA_IMPORTACAO,
  ROTULO_CODIFICACAO,
  ROTULO_DECISAO,
  ROTULO_DIAGNOSTICO,
  ROTULO_ETAPA_DA_IMPORTACAO,
  ROTULO_SEPARADOR,
  ROTULO_STATUS_ALUNO,
  STATUS_IMPORTAVEIS,
  zMapeamento,
  type CampoDaImportacao,
  type CodigoDeAviso,
  type DecisoesDaImportacao,
  type Diagnostico,
  type ImportacaoDetalhada,
  type LinhaDoRelatorio,
  type Mapeamento,
  type RevisaoDaImportacao,
  type StatusImportavel,
} from '@arena/shared/schemas';
import { dataCompleta, instanteCompleto, telefoneFormatado } from '../alunos/formatos.js';
import { ErroDeApi, ErroDeRede, mensagemDoErro } from '../api/cliente.js';
import { importacaoApi } from '../api/importacao.js';
import { Erro, EsqueletoDeCard, EsqueletoDeTabela } from '../ui/estados.jsx';
import { Icone } from '../ui/Icone.js';
import { Botao, Card, Escolhas, Kpi, Nota, Status, Tabela, Vazio } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import {
  decisaoDoValor,
  percentualGravado,
  ROTULO_RESULTADO,
  ROTULO_SITUACAO_DA_LINHA,
  separadorLegivel,
  SITUACAO_DA_ETAPA,
  SITUACAO_DA_LINHA,
  SITUACAO_DO_RESULTADO,
  tamanhoLegivel,
  valorDaDecisao,
} from './formatos.js';

/**
 * A tela de conferência — a que o protótipo NÃO tem, e o documento 09
 * desenha: os contadores no formato dos KPIs, a tabela com as linhas
 * diagnosticadas, o filtro por diagnóstico e o botão de confirmação
 * desabilitado enquanto houver bloqueio.
 *
 * Bloqueio aqui é da IMPORTAÇÃO — a coluna obrigatória sem campo no
 * de-para —, e quem diz é o servidor (`pendencias`). Linha bloqueada não
 * trava nada: ela fica de fora, e as outras entram.
 *
 * Três momentos na mesma tela, pela etapa:
 *
 *   analisada   de-para editável, decisão por linha, confirmar
 *   gravando    o progresso andando sozinho, e "retomar" se ele parar
 *   concluída   o resultado de cada linha; os valores da planilha já
 *               saíram do relatório, e o que fica aponta para a ficha
 *
 * Ver: Arena_backend/Docs/plano/09-importacao-csv.md
 */

type Filtro = 'todas' | Diagnostico;

/** Sem andar por este tempo, a gravação é dada como parada e "retomar" aparece. */
const PARADA_APOS_MS = 60_000;

/** Os avisos que pedem atenção antes de confirmar — os outros são informação. */
const AVISOS_GRAVES: ReadonlySet<CodigoDeAviso> = new Set<CodigoDeAviso>([
  'acentuacao_corrompida',
  'aspas_sem_fechamento',
  'arquivo_ja_importado',
  'base_mudou',
  'colunas_a_mais',
]);

function tipoDoErro(erro: unknown): 'rede' | 'permissao' | 'falha' {
  if (erro instanceof ErroDeRede) return 'rede';
  if (erro instanceof ErroDeApi && erro.ehPermissao) return 'permissao';
  return 'falha';
}

/** A mensagem do servidor, com o primeiro detalhe por linha quando houver. */
function mensagemComDetalhe(erro: unknown): string {
  const base = mensagemDoErro(erro);
  if (!(erro instanceof ErroDeApi) || !erro.campos) return base;
  const [chave, mensagens] = Object.entries(erro.campos)[0] ?? [];
  return chave && mensagens?.[0] ? `${base} ${capitalizar(chave)}: ${mensagens[0]}` : base;
}

const capitalizar = (texto: string) => texto.charAt(0).toUpperCase() + texto.slice(1);

export function PaginaDaImportacao() {
  const { id = '' } = useParams();
  const navegar = useNavigate();
  const cliente = useQueryClient();
  const avisar = useToast();
  const [filtro, setFiltro] = useState<Filtro>('todas');
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [confirmandoDescarte, setConfirmandoDescarte] = useState(false);

  const detalhe = useQuery({
    queryKey: ['importacoes', id],
    queryFn: () => importacaoApi.detalhe(id),
    // Gravando, o progresso anda sem F5: o job trabalha em lotes de cem.
    refetchInterval: (consulta) => (consulta.state.data?.etapa === 'gravando' ? 2_000 : false),
  });

  const linhas = useInfiniteQuery({
    queryKey: ['importacoes', id, 'linhas', filtro],
    queryFn: ({ pageParam }: { pageParam: number | undefined }) =>
      importacaoApi.linhas(id, {
        diagnostico: filtro === 'todas' ? undefined : filtro,
        apos: pageParam,
        limite: 100,
      }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (ultima) => ultima.proximo ?? undefined,
  });

  const aoMudar = (atualizada: ImportacaoDetalhada) => {
    cliente.setQueryData(['importacoes', id], atualizada);
    void cliente.invalidateQueries({ queryKey: ['importacoes', id, 'linhas'] });
    void cliente.invalidateQueries({ queryKey: ['importacoes', 'lista'] });
  };
  const decidir = useMutation({
    mutationFn: (pedido: DecisoesDaImportacao) => importacaoApi.decidir(id, pedido),
    onSuccess: aoMudar,
  });
  const revisar = useMutation({
    mutationFn: (revisao: RevisaoDaImportacao) => importacaoApi.revisar(id, revisao),
    onSuccess: aoMudar,
  });
  const confirmar = useMutation({
    mutationFn: () => importacaoApi.confirmar(id),
    onSuccess: aoMudar,
  });
  const retomar = useMutation({ mutationFn: () => importacaoApi.retomar(id), onSuccess: aoMudar });
  const descartar = useMutation({
    mutationFn: () => importacaoApi.descartar(id),
    onSuccess: aoMudar,
  });

  // A etapa mudou sozinha (o job terminou): o relatório ganha os
  // resultados, e a lista de alunos, os cadastros novos.
  const etapa = detalhe.data?.etapa;
  const etapaAnterior = useRef(etapa);
  useEffect(() => {
    const anterior = etapaAnterior.current;
    etapaAnterior.current = etapa;
    if (!anterior || anterior === etapa) return;
    void cliente.invalidateQueries({ queryKey: ['importacoes', id, 'linhas'] });
    if (etapa === 'concluida') {
      void cliente.invalidateQueries({ queryKey: ['alunos'] });
      avisar('Importação concluída.');
    }
  }, [etapa, id, cliente, avisar]);

  // "Parada" é não andar por um minuto — o job caiu e não voltou.
  const andamento = detalhe.data
    ? Object.values(detalhe.data.progresso).reduce((soma, n) => soma + n, 0)
    : 0;
  const [paradaDesde, setParadaDesde] = useState(() => Date.now());
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => setParadaDesde(Date.now()), [andamento, etapa]);
  useEffect(() => {
    if (etapa !== 'gravando') return;
    const relogio = setInterval(() => setAgora(Date.now()), 5_000);
    return () => clearInterval(relogio);
  }, [etapa]);

  async function executar(acao: () => Promise<unknown>) {
    setErroGeral(null);
    try {
      await acao();
    } catch (erro) {
      setErroGeral(mensagemComDetalhe(erro));
      // A confirmação pode ter mudado linhas antes de recusar (a base mudou).
      void cliente.invalidateQueries({ queryKey: ['importacoes', id] });
    }
  }

  if (detalhe.isPending) {
    return (
      <>
        <div className="page-head">
          <div>
            <h1>Conferência da importação</h1>
          </div>
        </div>
        <div className="grid g4">
          {[0, 1, 2, 3].map((i) => (
            <EsqueletoDeCard key={i} linhas={2} />
          ))}
        </div>
        <EsqueletoDeTabela colunas={7} />
      </>
    );
  }

  if (detalhe.isError) {
    return (
      <Erro
        tipo={tipoDoErro(detalhe.error)}
        detalhe={mensagemDoErro(detalhe.error)}
        codigo={detalhe.error instanceof ErroDeApi ? detalhe.error.requestId : undefined}
        onTentarDeNovo={() => void detalhe.refetch()}
      />
    );
  }

  const d = detalhe.data;
  const editavel = d.etapa === 'analisada';
  const itens = linhas.data?.pages.flatMap((p) => p.itens) ?? [];
  const totalDoFiltro = linhas.data?.pages[0]?.total ?? 0;
  const percentual = percentualGravado(d);
  const parada = d.etapa === 'gravando' && agora - paradaDesde > PARADA_APOS_MS;

  const opcoesDoFiltro: { valor: Filtro; rotulo: string }[] = [
    { valor: 'todas', rotulo: `Todas · ${d.contadores.total}` },
    ...DIAGNOSTICOS.filter((dg) => d.porDiagnostico[dg] > 0 || dg === filtro).map((dg) => ({
      valor: dg,
      rotulo: `${ROTULO_DIAGNOSTICO[dg]} · ${d.porDiagnostico[dg]}`,
    })),
  ];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Conferência da importação</h1>
          <p>
            {d.nomeArquivo} · {tamanhoLegivel(d.tamanhoBytes)} · {ROTULO_CODIFICACAO[d.codificacao]}{' '}
            · {separadorLegivel(ROTULO_SEPARADOR[d.separador], d.separador)} · enviada
            {d.autor ? ` por ${d.autor}` : ''} em {instanteCompleto(d.criadaEm, d.fuso)}
          </p>
        </div>
        <div className="head-actions">
          <Status situacao={SITUACAO_DA_ETAPA[d.etapa]}>
            {ROTULO_ETAPA_DA_IMPORTACAO[d.etapa]}
          </Status>
          <Botao variante="contorno" onClick={() => navegar('/alunos')}>
            Voltar para alunos
          </Botao>
          {editavel && !confirmandoDescarte && (
            <Botao variante="fantasma" onClick={() => setConfirmandoDescarte(true)}>
              Descartar
            </Botao>
          )}
          {editavel && (
            <Botao
              icone="check"
              onClick={() => void executar(() => confirmar.mutateAsync())}
              disabled={d.pendencias.length > 0 || confirmar.isPending}
            >
              {confirmar.isPending ? 'Conferindo…' : 'Confirmar e gravar'}
            </Botao>
          )}
        </div>
      </div>

      {confirmandoDescarte && editavel && (
        <div className="aviso conflito" role="alert">
          <Icone nome="info" />
          <div>
            <b>Descartar esta análise?</b> Nenhum aluno foi gravado, e os valores da planilha saem
            do relatório na hora. Para importar depois, é só enviar o arquivo de novo.
          </div>
          <span className="fechar" style={{ display: 'flex', gap: 8 }}>
            <Botao variante="fantasma" pequeno onClick={() => setConfirmandoDescarte(false)}>
              Manter
            </Botao>
            <Botao
              pequeno
              disabled={descartar.isPending}
              onClick={() =>
                void executar(async () => {
                  await descartar.mutateAsync();
                  setConfirmandoDescarte(false);
                })
              }
            >
              Descartar
            </Botao>
          </span>
        </div>
      )}

      <div className="grid g4">
        <Kpi
          valor={d.contadores.validas}
          rotulo="Linhas válidas"
          tendencia="estavel"
          variacao="serão criadas"
        />
        <Kpi
          valor={d.contadores.duplicadas}
          rotulo="Com duplicidade"
          tendencia="estavel"
          variacao="decisão por linha"
        />
        <Kpi
          valor={d.contadores.bloqueadas}
          rotulo="Precisam de correção"
          tendencia={d.contadores.bloqueadas > 0 ? 'baixa' : 'estavel'}
          variacao="ficam de fora"
        />
        <Kpi
          valor={d.contadores.normalizadas}
          rotulo="Aceitas com ajuste"
          tendencia="estavel"
          variacao="informativo"
        />
      </div>

      {editavel &&
        d.pendencias.map((p) => (
          <div key={p.codigo + p.mensagem} className="aviso conflito" role="alert">
            <Icone nome="info" />
            <div>
              <b>Antes de confirmar:</b> {p.mensagem}
            </div>
          </div>
        ))}

      {erroGeral && (
        <div className="aviso conflito" role="alert">
          <Icone nome="info" />
          <div>{erroGeral}</div>
        </div>
      )}

      {d.avisos.map((a) => (
        <div
          key={a.codigo}
          className={['aviso', AVISOS_GRAVES.has(a.codigo) && 'conflito'].filter(Boolean).join(' ')}
          role="status"
        >
          <Icone nome="info" />
          <div>
            {a.mensagem}
            {a.linhas && a.linhas.length > 0 ? ` Linhas: ${a.linhas.join(', ')}.` : ''}
          </div>
        </div>
      ))}

      {editavel && (d.aGravar.criar > 0 || d.aGravar.atualizar > 0) && (
        <div className="aviso livre" role="status">
          <Icone nome="check" />
          <div>
            Ao confirmar: <b>{d.aGravar.criar}</b> cadastro(s) novo(s) e{' '}
            <b>{d.aGravar.atualizar}</b> atualização(ões). O servidor confere a base de novo antes
            de gravar — se alguém entrou nela desde a análise, a linha volta para você decidir.
          </div>
        </div>
      )}

      {d.etapa === 'gravando' && (
        <Card titulo="Gravando" dica={`${percentual}%`}>
          <div className="progresso-da-importacao" aria-label={`${percentual}% gravado`}>
            <div style={{ width: `${percentual}%` }} />
          </div>
          <p className="sub" style={{ marginTop: 10 }}>
            {d.progresso.criados} criados · {d.progresso.atualizados} atualizados ·{' '}
            {d.progresso.pulados} pulados · {d.progresso.falhas} não entraram ·{' '}
            {d.progresso.pendentes} na fila
          </p>
          {parada && (
            <div className="aviso" role="status" style={{ marginTop: 12 }}>
              <Icone nome="info" />
              <div>
                A gravação não anda há mais de um minuto. Retomar é seguro: o que já entrou não
                entra de novo.
              </div>
              <span className="fechar">
                <Botao
                  variante="contorno"
                  pequeno
                  disabled={retomar.isPending}
                  onClick={() => void executar(() => retomar.mutateAsync())}
                >
                  Retomar gravação
                </Botao>
              </span>
            </div>
          )}
        </Card>
      )}

      {d.etapa === 'concluida' && (
        <div className="aviso livre" role="status">
          <Icone nome="check" />
          <div>
            <b>
              Importação concluída
              {d.concluidaEm ? ` em ${instanteCompleto(d.concluidaEm, d.fuso)}` : ''}.
            </b>{' '}
            {d.progresso.criados} cadastro(s) novo(s), {d.progresso.atualizados} atualizado(s),{' '}
            {d.progresso.pulados} linha(s) pulada(s)
            {d.progresso.falhas > 0 ? `, ${d.progresso.falhas} não entraram` : ''}. Os alunos
            importados estão com o aceite do termo pendente: a ficha de cada um mostra a pendência
            até ele aceitar.
          </div>
        </div>
      )}

      {d.etapa === 'descartada' && (
        <div className="aviso" role="status">
          <Icone nome="info" />
          <div>Importação descartada. Nenhum aluno foi gravado.</div>
        </div>
      )}

      {editavel && !d.dadosApagados && (
        <DeParaDaImportacao
          importacao={d}
          salvando={revisar.isPending}
          onRevisar={(revisao) => void executar(() => revisar.mutateAsync(revisao))}
        />
      )}

      <Card
        titulo="Relatório por linha"
        dica={linhas.isPending ? '' : `${itens.length} de ${totalDoFiltro} linha(s)`}
      >
        <div style={{ marginBottom: 12 }}>
          <Escolhas
            aria-label="Filtrar por diagnóstico"
            opcoes={opcoesDoFiltro}
            valor={filtro}
            onChange={setFiltro}
          />
        </div>

        {d.dadosApagados && (
          <Nota>
            Os valores da planilha saíram deste relatório quando a importação terminou — cada aluno
            já os tem na própria ficha. Ficam o número da linha, o diagnóstico e o resultado: é o
            que responde "de onde veio este cadastro".
          </Nota>
        )}

        {linhas.isPending && <EsqueletoDeTabela colunas={7} />}

        {linhas.isError && (
          <Erro
            tipo={tipoDoErro(linhas.error)}
            detalhe={mensagemDoErro(linhas.error)}
            onTentarDeNovo={() => void linhas.refetch()}
          />
        )}

        {linhas.data && itens.length === 0 && <Vazio>Nenhuma linha com este diagnóstico.</Vazio>}

        {linhas.data && itens.length > 0 && (
          <>
            <Tabela
              colunas={[
                'Linha',
                'Aluno',
                'Telefone',
                'Nascimento',
                'Diagnóstico',
                'Já cadastrado',
                editavel ? 'Decisão' : 'Resultado',
              ]}
            >
              {itens.map((linha) => (
                <LinhaDaTabela
                  key={linha.numero}
                  linha={linha}
                  importacao={d}
                  decidindo={decidir.isPending}
                  onDecidir={(valor) => {
                    const { decisao, alunoAlvoId } = decisaoDoValor(valor);
                    void executar(() =>
                      decidir.mutateAsync({
                        decisoes: [
                          { linha: linha.numero, decisao, ...(alunoAlvoId ? { alunoAlvoId } : {}) },
                        ],
                      }),
                    );
                  }}
                />
              ))}
            </Tabela>

            {linhas.hasNextPage && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '14px 0 2px' }}>
                <Botao
                  variante="contorno"
                  pequeno
                  onClick={() => void linhas.fetchNextPage()}
                  disabled={linhas.isFetchingNextPage}
                >
                  {linhas.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
                </Botao>
              </div>
            )}
          </>
        )}
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------

function LinhaDaTabela({
  linha,
  importacao,
  decidindo,
  onDecidir,
}: {
  linha: LinhaDoRelatorio;
  importacao: ImportacaoDetalhada;
  decidindo: boolean;
  onDecidir: (valor: string) => void;
}) {
  const editavel = importacao.etapa === 'analisada';
  const { dados } = linha;

  return (
    <tr>
      <td className="num">{linha.numero}</td>
      <td>
        {dados?.nome ?? '—'}
        {dados?.email && (
          <small style={{ display: 'block', color: 'var(--dim)', fontSize: 11.5 }}>
            {dados.email}
          </small>
        )}
      </td>
      <td>{dados?.telefone ? telefoneFormatado(dados.telefone) : '—'}</td>
      <td>{dados?.nascimento ? dataCompleta(dados.nascimento) : '—'}</td>
      <td>
        <Status situacao={SITUACAO_DA_LINHA[linha.situacao]}>
          {ROTULO_SITUACAO_DA_LINHA[linha.situacao]}
        </Status>
        {linha.ocorrencias.length > 0 && (
          <ul className="ocorrencias-da-linha">
            {linha.ocorrencias.map((o, i) => (
              <li key={i}>{o.mensagem}</li>
            ))}
          </ul>
        )}
      </td>
      <td>
        {linha.candidatos.length === 0
          ? '—'
          : linha.candidatos.map((c) => (
              <div key={c.id}>
                <Link className="link" to={`/alunos?aluno=${c.id}`}>
                  {c.nome}
                </Link>{' '}
                <small style={{ color: 'var(--dim)' }}>{ROTULO_STATUS_ALUNO[c.status]}</small>
              </div>
            ))}
      </td>
      <td>
        {editavel ? (
          <select
            className="selecao-compacta"
            aria-label={`Decisão da linha ${linha.numero}`}
            value={valorDaDecisao(linha.decisao, linha.alunoAlvoId)}
            disabled={decidindo || linha.decisoesPossiveis.length === 1}
            onChange={(e) => onDecidir(e.target.value)}
          >
            {opcoesDaDecisao(linha).map((o) => (
              <option key={o.valor} value={o.valor}>
                {o.rotulo}
              </option>
            ))}
          </select>
        ) : linha.resultado ? (
          <>
            <Status situacao={SITUACAO_DO_RESULTADO[linha.resultado]}>
              {ROTULO_RESULTADO[linha.resultado]}
            </Status>
            {linha.alunoId && (
              <Link
                className="link"
                to={`/alunos?aluno=${linha.alunoId}`}
                style={{ display: 'block', marginTop: 4 }}
              >
                Abrir ficha
              </Link>
            )}
            {linha.erro && (
              <small style={{ display: 'block', color: 'var(--red-lt)', marginTop: 4 }}>
                {linha.erro}
              </small>
            )}
          </>
        ) : (
          <span className="sub">
            {importacao.etapa === 'gravando' ? 'Na fila' : ROTULO_DECISAO[linha.decisao]}
          </span>
        )}
      </td>
    </tr>
  );
}

/** As escolhas da linha: "atualizar" vira uma opção por cadastro candidato. */
function opcoesDaDecisao(linha: LinhaDoRelatorio): { valor: string; rotulo: string }[] {
  return linha.decisoesPossiveis.flatMap((decisao) => {
    if (decisao === 'atualizar') {
      return linha.candidatos.map((c) => ({
        valor: valorDaDecisao('atualizar', c.id),
        rotulo: `Atualizar ${c.nome}`,
      }));
    }
    if (decisao === 'criar') {
      return [
        {
          valor: 'criar',
          rotulo: linha.situacao === 'duplicada' ? 'Criar outro cadastro' : 'Criar cadastro',
        },
      ];
    }
    return [{ valor: decisao, rotulo: ROTULO_DECISAO[decisao] }];
  });
}

// ---------------------------------------------------------------------

/**
 * O de-para, com correção manual. A mudança é um RASCUNHO até "reanalisar":
 * trocar uma coluna reanalisa a planilha inteira no servidor, e a linha
 * cujo diagnóstico mudou volta à decisão padrão — isso não pode acontecer
 * a cada clique num select.
 */
function DeParaDaImportacao({
  importacao,
  salvando,
  onRevisar,
}: {
  importacao: ImportacaoDetalhada;
  salvando: boolean;
  onRevisar: (revisao: RevisaoDaImportacao) => void;
}) {
  const doServidor: Mapeamento = importacao.colunas.map((c) => c.campo);
  const chaveDoServidor = JSON.stringify(doServidor);
  const [rascunho, setRascunho] = useState<Mapeamento>(doServidor);

  // O servidor respondeu com outro de-para (a revisão salvou): o rascunho acompanha.
  useEffect(() => {
    setRascunho(JSON.parse(chaveDoServidor) as Mapeamento);
  }, [chaveDoServidor]);

  const validacao = zMapeamento.safeParse(rascunho);
  const erros = new Map<number, string>();
  if (!validacao.success) {
    for (const issue of validacao.error.issues) {
      if (typeof issue.path[0] === 'number') erros.set(issue.path[0], issue.message);
    }
  }
  const mudou = JSON.stringify(rascunho) !== chaveDoServidor;
  const faltando = CAMPOS_OBRIGATORIOS_DA_IMPORTACAO.filter((campo) => !rascunho.includes(campo));

  return (
    <Card
      titulo="De-para das colunas"
      dica={
        importacao.origemDoMapeamento === 'automatico'
          ? 'detectado pelo título das colunas'
          : importacao.origemDoMapeamento === 'importacao_anterior'
            ? 'o mesmo da última importação desta planilha'
            : 'corrigido à mão'
      }
      acao={
        <Botao
          pequeno
          variante="contorno"
          disabled={!mudou || !validacao.success || salvando}
          onClick={() => onRevisar({ mapeamento: rascunho })}
        >
          {salvando ? 'Reanalisando…' : 'Reanalisar com este de-para'}
        </Botao>
      }
    >
      <Tabela colunas={['Coluna da planilha', 'Exemplos', 'Campo do cadastro']}>
        {importacao.colunas.map((coluna) => (
          <tr key={coluna.indice}>
            <td>
              <b>{coluna.titulo || `Coluna ${coluna.indice + 1} (sem título)`}</b>
            </td>
            <td className="sub">
              {coluna.exemplos.length > 0 ? coluna.exemplos.join(' · ') : '—'}
            </td>
            <td>
              <select
                className="selecao-compacta"
                aria-label={`Campo da coluna ${coluna.titulo || coluna.indice + 1}`}
                value={rascunho[coluna.indice] ?? ''}
                onChange={(e) => {
                  const campo = (e.target.value || null) as CampoDaImportacao | null;
                  setRascunho((atual) => atual.map((c, i) => (i === coluna.indice ? campo : c)));
                }}
              >
                <option value="">Ignorar esta coluna</option>
                {CAMPOS_DA_IMPORTACAO.map((campo) => (
                  <option key={campo} value={campo}>
                    {ROTULO_CAMPO_DA_IMPORTACAO[campo]}
                    {(CAMPOS_OBRIGATORIOS_DA_IMPORTACAO as readonly string[]).includes(campo)
                      ? ' *'
                      : ''}
                  </option>
                ))}
              </select>
              {erros.get(coluna.indice) && (
                <small
                  role="alert"
                  style={{ display: 'block', color: 'var(--red-lt)', marginTop: 4 }}
                >
                  {erros.get(coluna.indice)}
                </small>
              )}
            </td>
          </tr>
        ))}
      </Tabela>

      <div
        style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginTop: 14 }}
      >
        <span className="sub">Situação de quem entra sem coluna de situação:</span>
        <select
          className="selecao-compacta"
          aria-label="Situação padrão"
          value={importacao.statusPadrao}
          disabled={salvando}
          onChange={(e) => onRevisar({ statusPadrao: e.target.value as StatusImportavel })}
        >
          {STATUS_IMPORTAVEIS.map((s) => (
            <option key={s} value={s}>
              {ROTULO_STATUS_ALUNO[s]}
            </option>
          ))}
        </select>
        {faltando.length > 0 && (
          <span className="sub" style={{ color: 'var(--red-lt)' }}>
            Falta ligar: {faltando.map((c) => ROTULO_CAMPO_DA_IMPORTACAO[c]).join(', ')}.
          </span>
        )}
      </div>
    </Card>
  );
}
